import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Video, Globe, Zap, Shield, TrendingUp, Users, Star, Check, MessageCircle, Radio, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Logo, { LogoIcon } from './Logo';

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const features = [
    {
      icon: <MessageCircle className="w-7 h-7" />,
      title: "Smart Messaging",
      description: "Emotion-tinted bubbles, doodle canvas, and pulse reactions",
      color: "#00F0FF"
    },
    {
      icon: <Video className="w-7 h-7" />,
      title: "HD Video Calls",
      description: "Crystal clear calls with screen sharing & translation",
      color: "#FFE800"
    },
    {
      icon: <Radio className="w-7 h-7" />,
      title: "Go Live",
      description: "Stream to thousands with real-time chat & reactions",
      color: "#FF2A2A"
    },
    {
      icon: <Globe className="w-7 h-7" />,
      title: "Break Barriers",
      description: "Real-time AI translation in 20+ languages",
      color: "#00FF66"
    },
    {
      icon: <Shield className="w-7 h-7" />,
      title: "Secure & Private",
      description: "End-to-end encryption for all conversations",
      color: "#A78BFA"
    },
    {
      icon: <Sparkles className="w-7 h-7" />,
      title: "AI Powered",
      description: "Voice transcription, smart replies & more",
      color: "#F472B6"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      role: "CEO, TechStart Inc",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=face",
      content: "Pulsate Global transformed how our global team communicates. The live translation is a game-changer!",
      rating: 5
    },
    {
      name: "Marcus Johnson",
      role: "Remote Team Lead",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      content: "Screen sharing during video calls saved us countless hours. Love the emotion-tinted messages!",
      rating: 5
    },
    {
      name: "Emily Rodriguez",
      role: "Content Creator",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      content: "The livestreaming feature helped me grow my audience 10x. The engagement tools are incredible!",
      rating: 5
    }
  ];

  const stats = [
    { value: "50K+", label: "Active Users" },
    { value: "1M+", label: "Messages Daily" },
    { value: "20+", label: "Languages" },
    { value: "99.9%", label: "Uptime" }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#030305]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#030305]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo size="sm" animate={true} />
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm text-gray-400 hover:text-white transition-colors">Features</a>
            <a href="#testimonials" className="text-sm text-gray-400 hover:text-white transition-colors">Reviews</a>
            <a href="#pricing" className="text-sm text-gray-400 hover:text-white transition-colors">Pricing</a>
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/auth')}
              className="text-sm font-medium text-white hover:text-[#00F0FF] transition-colors"
            >
              Sign In
            </button>
            <motion.button
              data-testid="nav-get-started"
              onClick={() => navigate('/auth')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 rounded-full text-sm font-bold bg-[#00F0FF] text-black"
            >
              Get Started
            </motion.button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Animated background effects */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 left-1/4 w-[500px] h-[500px] bg-[#FF2A2A] rounded-full blur-[200px] opacity-20 animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#00F0FF] rounded-full blur-[180px] opacity-15" />
          <div className="absolute top-1/2 left-1/2 w-[300px] h-[300px] bg-[#FFE800] rounded-full blur-[150px] opacity-10" />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex-1 text-center lg:text-left"
            >
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 bg-[#FF2A2A]/10 border border-[#FF2A2A]/30"
              >
                <span className="w-2 h-2 rounded-full bg-[#FF2A2A] animate-pulse" />
                <span className="text-sm font-semibold text-[#FF2A2A]">Live Now: 2.5K+ Streamers</span>
              </motion.div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-6 leading-[1.1] tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                <span className="text-white">Your Social</span>
                <br />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#FF2A2A] via-[#FFE800] to-[#00F0FF]">
                  Universe
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl mb-8 text-gray-400 max-w-xl mx-auto lg:mx-0" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Chat, call, stream, and connect with people worldwide. 
                Real-time translation breaks every barrier.
              </p>
              
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <motion.button
                  data-testid="hero-get-started-button"
                  onClick={() => navigate('/auth')}
                  whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(255,42,42,0.5)' }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 rounded-full font-bold text-lg flex items-center gap-2 bg-gradient-to-r from-[#FF2A2A] to-[#FFE800] text-black shadow-lg shadow-[#FF2A2A]/30"
                >
                  Join Free
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
                
                <motion.button
                  data-testid="watch-demo-button"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-4 rounded-full font-bold text-lg bg-white/5 border border-white/20 text-white backdrop-blur-xl hover:bg-white/10 transition-colors"
                >
                  Watch Demo
                </motion.button>
              </div>
              
              <div className="flex items-center gap-6 mt-10 justify-center lg:justify-start">
                <div className="flex -space-x-3">
                  {['494790108377-be9c29b29330', '507003211169-0a1dd7228f2d', '438761681033-6461ffad8d80', '472099645785-5658abf4ff4e', '544005313-f89d122e7772'].map((id, i) => (
                    <img
                      key={i}
                      src={`https://images.unsplash.com/photo-1${id}?w=40&h=40&fit=crop&crop=face`}
                      alt="User"
                      className="w-10 h-10 rounded-full border-2 border-[#030305] object-cover"
                    />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-[#FFE800] text-[#FFE800]" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-500">50,000+ happy users</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="flex-1 relative"
            >
              {/* Phone mockup with app preview */}
              <div className="relative mx-auto w-[280px] sm:w-[320px]">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#FF2A2A]/30 via-[#FFE800]/20 to-[#00F0FF]/30 rounded-[3rem] blur-3xl scale-110" />
                
                {/* Phone frame */}
                <div className="relative bg-[#0F0F16] rounded-[3rem] p-3 border border-white/10 shadow-2xl">
                  {/* Screen */}
                  <div className="bg-[#030305] rounded-[2.5rem] overflow-hidden">
                    {/* Status bar */}
                    <div className="flex items-center justify-between px-6 py-3 text-xs text-white/60">
                      <span>9:41</span>
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-2 border border-white/60 rounded-sm">
                          <div className="w-2/3 h-full bg-[#00FF66] rounded-sm" />
                        </div>
                      </div>
                    </div>
                    
                    {/* App content preview */}
                    <div className="px-4 pb-6">
                      <div className="flex items-center gap-3 mb-4">
                        <LogoIcon size="xs" animate={true} />
                        <span className="text-white font-bold text-sm" style={{ fontFamily: 'Unbounded, sans-serif' }}>Pulsate</span>
                      </div>
                      
                      {/* Feed preview */}
                      <div className="space-y-3">
                        {/* Live stream card */}
                        <div className="bg-[#08080C] rounded-2xl p-3 border border-white/5">
                          <div className="relative mb-2">
                            <img 
                              src="https://images.unsplash.com/photo-1652512455871-f88e3b1b9039?w=300&h=150&fit=crop"
                              alt="Live stream"
                              className="w-full h-24 object-cover rounded-xl"
                            />
                            <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FF2A2A] text-white flex items-center gap-1">
                              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                              LIVE
                            </span>
                            <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full text-[10px] bg-black/50 text-white backdrop-blur-sm">
                              1.2K watching
                            </span>
                          </div>
                          <p className="text-xs font-semibold text-white truncate">Gaming Night Stream</p>
                          <p className="text-[10px] text-gray-500">@streamer_pro</p>
                        </div>
                        
                        {/* Chat preview */}
                        <div className="bg-[#08080C] rounded-2xl p-3 border border-white/5">
                          <div className="flex items-center gap-2 mb-2">
                            <img 
                              src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=32&h=32&fit=crop&crop=face"
                              alt="User"
                              className="w-6 h-6 rounded-full object-cover"
                            />
                            <span className="text-xs font-medium text-white">Sarah Chen</span>
                            <span className="text-[10px] text-gray-500">2m ago</span>
                          </div>
                          <div className="bg-[#00F0FF]/10 rounded-xl rounded-tl-none px-3 py-2 inline-block">
                            <p className="text-xs text-white">Hey! Ready for the call? 📞</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Bottom nav preview */}
                    <div className="flex justify-around items-center py-4 px-6 bg-[#08080C]/80 border-t border-white/5">
                      <div className="w-6 h-6 rounded-full bg-[#00F0FF]/20 flex items-center justify-center">
                        <MessageCircle className="w-3 h-3 text-[#00F0FF]" />
                      </div>
                      <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center">
                        <Video className="w-3 h-3 text-gray-500" />
                      </div>
                      <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center">
                        <Radio className="w-3 h-3 text-gray-500" />
                      </div>
                      <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center">
                        <Users className="w-3 h-3 text-gray-500" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-[#08080C] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-black mb-2 bg-clip-text text-transparent bg-gradient-to-r from-[#FF2A2A] to-[#00F0FF]" style={{ fontFamily: 'Unbounded, sans-serif' }}>{stat.value}</div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-[#030305]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="text-4xl sm:text-5xl font-black mb-4 text-white"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Why Choose Pulsate Global?
            </motion.h2>
            <p className="text-lg sm:text-xl text-gray-500">
              Packed with features that make communication effortless and delightful
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -5, boxShadow: '0 0 30px rgba(0,240,255,0.1)' }}
                className="bg-[#08080C] p-6 sm:p-8 rounded-2xl border border-white/5"
              >
                <div
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${feature.color}15`, color: feature.color }}
                >
                  {feature.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-3 text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-gray-500 text-sm sm:text-base">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-[#08080C]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl sm:text-4xl font-black text-center mb-12 text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Loved by Teams Worldwide
          </h2>
          
          <motion.div
            key={activeTestimonial}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="bg-gradient-to-br from-[#00F0FF]/20 to-[#FFE800]/20 p-6 sm:p-8 rounded-3xl border border-white/10"
          >
            <div className="flex items-center gap-4 mb-6">
              <img
                src={testimonials[activeTestimonial].avatar}
                alt={testimonials[activeTestimonial].name}
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-full border-2 border-[#00F0FF]/30"
              />
              <div>
                <div className="font-bold text-base sm:text-lg text-white">{testimonials[activeTestimonial].name}</div>
                <div className="text-gray-400 text-sm">{testimonials[activeTestimonial].role}</div>
              </div>
              <div className="ml-auto flex gap-1">
                {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 sm:w-5 sm:h-5 fill-[#FFE800] text-[#FFE800]" />
                ))}
              </div>
            </div>
            <p className="text-lg sm:text-xl leading-relaxed text-gray-300">"{testimonials[activeTestimonial].content}"</p>
          </motion.div>
          
          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveTestimonial(idx)}
                className={`w-3 h-3 rounded-full transition-all ${
                  idx === activeTestimonial ? 'w-8 bg-[#00F0FF]' : 'bg-gray-700'
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Mobile App Download Section */}
      <section className="py-20 bg-[#030305] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="flex-1"
            >
              <h2 className="text-3xl sm:text-4xl font-black mb-4 text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Take Pulsate Everywhere
              </h2>
              <p className="text-lg text-gray-400 mb-8">
                Download our mobile app and stay connected on the go. Available for iOS and Android.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <motion.a
                  href="https://play.google.com/store"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center gap-3 px-6 py-3 bg-[#08080C] border border-white/10 rounded-xl hover:border-[#00F0FF]/50 transition-colors"
                >
                  <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" fill="#00F0FF"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">Get it on</div>
                    <div className="text-white font-semibold">Google Play</div>
                  </div>
                </motion.a>
                
                <motion.a
                  href="https://apps.apple.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center gap-3 px-6 py-3 bg-[#08080C] border border-white/10 rounded-xl hover:border-[#00F0FF]/50 transition-colors"
                >
                  <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z"/>
                  </svg>
                  <div className="text-left">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wider">Download on the</div>
                    <div className="text-white font-semibold">App Store</div>
                  </div>
                </motion.a>
              </div>
              
              <div className="mt-8 flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-[#00FF66]" />
                  <span className="text-sm text-gray-400">Free to download</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-[#00FF66]" />
                  <span className="text-sm text-gray-400">No ads</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-[#00FF66]" />
                  <span className="text-sm text-gray-400">Offline mode</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="flex-1 flex justify-center"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FF2A2A]/20 via-[#FFE800]/10 to-[#00F0FF]/20 rounded-3xl blur-3xl" />
                <div className="relative bg-[#0F0F16] p-4 rounded-3xl border border-white/10">
                  <img 
                    src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=300&h=500&fit=crop"
                    alt="Pulsate Mobile App"
                    className="w-48 h-80 object-cover rounded-2xl"
                  />
                  <div className="absolute -right-4 -bottom-4 bg-[#00F0FF] text-black font-bold text-xs px-3 py-1.5 rounded-full">
                    Coming Soon!
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#030305] via-[#00F0FF]/10 to-[#030305]">
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-4xl sm:text-5xl font-black mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ready to Transform Your Communication?
            </h2>
            <p className="text-lg sm:text-xl mb-8 text-gray-400">
              Join 50,000+ users who are already experiencing the future of messaging
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <motion.button
                data-testid="cta-get-started-button"
                onClick={() => navigate('/auth')}
                whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(0,240,255,0.4)' }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-5 rounded-full font-bold text-lg flex items-center gap-2 bg-[#00F0FF] text-black shadow-lg"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5" />
              </motion.button>
              
              <p className="text-sm text-gray-500">
                No credit card required • Free plan forever
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#08080C] border-t border-white/5 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <Logo size="sm" animate={false} />
              <p className="text-gray-500 text-sm mt-4">Your social universe awaits. Connect, stream, and break barriers.</p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4 text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Product</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="#features" className="hover:text-[#00F0FF] transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-[#00F0FF] transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-[#00F0FF] transition-colors">Enterprise</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4 text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Company</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="#" className="hover:text-[#00F0FF] transition-colors">About</a></li>
                <li><a href="#" className="hover:text-[#00F0FF] transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-[#00F0FF] transition-colors">Careers</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold mb-4 text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Support</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li><a href="#" className="hover:text-[#00F0FF] transition-colors">Help Center</a></li>
                <li><a href="#" className="hover:text-[#00F0FF] transition-colors">Contact Us</a></li>
                <li><a href="#" className="hover:text-[#00F0FF] transition-colors">Status</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/5 pt-8 text-center text-sm text-gray-500">
            <p>© 2026 Pulsate Global. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;